(() => {
  if (customElements.get('cv-product-page')) return;
  const text = (element, value) => { if (element && element.textContent !== value) element.textContent = value; };
  const normal = (value) => String(value || '').trim().toLowerCase();

  class CvThumbnailRail extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.slider = this.querySelector('ul');
      this.previous = this.querySelector('[name="previous"]');
      this.next = this.querySelector('[name="next"]');
      this.desktop = matchMedia('(min-width:990px)');
      this.addEventListener('click', (event) => {
        const control = event.target.closest('.slider-button');
        if (!control) return;
        const amount = (this.desktop.matches ? this.slider.clientHeight : this.slider.clientWidth) * .75;
        this.slider.scrollBy({ [this.desktop.matches ? 'top' : 'left']: amount * (control.name === 'previous' ? -1 : 1), behavior: this.behavior });
      }, options);
      this.addEventListener('keydown', (event) => {
        if (!event.target.closest('.thumbnail')) return;
        const keys = this.desktop.matches ? ['ArrowUp', 'ArrowDown'] : ['ArrowLeft', 'ArrowRight'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const buttons = [...this.querySelectorAll('.thumbnail')].filter(button => button.getClientRects().length);
        const next = buttons[buttons.indexOf(event.target.closest('.thumbnail')) + (event.key === keys[0] ? -1 : 1)];
        if (next) { next.focus({ preventScroll: true }); next.click(); }
      }, options);
      this.slider.addEventListener('scroll', () => this.initPages(), options);
      this.desktop.addEventListener('change', () => this.initPages(), options);
      this.resize = new ResizeObserver(() => this.initPages());
      this.resize.observe(this.slider);
      this.initPages();
    }
    get behavior() { return matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth'; }
    initPages() {
      if (!this.slider) return;
      const vertical = this.desktop.matches;
      const position = vertical ? this.slider.scrollTop : this.slider.scrollLeft;
      const extent = vertical ? this.slider.scrollHeight - this.slider.clientHeight : this.slider.scrollWidth - this.slider.clientWidth;
      this.previous.disabled = position <= 2;
      this.next.disabled = position >= extent - 2;
      this.previous.setAttribute('aria-label', 'Previous image thumbnails');
      this.next.setAttribute('aria-label', 'Next image thumbnails');
    }
    resetPages() { this.initPages(); }
    isSlideVisible(item) {
      const frame = this.slider.getBoundingClientRect();
      const rect = item.getBoundingClientRect();
      return this.desktop.matches ? rect.top >= frame.top && rect.bottom <= frame.bottom : rect.left >= frame.left && rect.right <= frame.right;
    }
    ensureVisible(item) {
      if (!item || this.isSlideVisible(item)) return;
      const frame = this.slider.getBoundingClientRect();
      const rect = item.getBoundingClientRect();
      const delta = this.desktop.matches ? rect.top - frame.top - 5 : rect.left - frame.left - 5;
      this.slider.scrollBy({ [this.desktop.matches ? 'top' : 'left']: delta, behavior: this.behavior });
    }
    disconnectedCallback() { this.events?.abort(); this.resize?.disconnect(); this.events = null; }
  }
  customElements.define('cv-thumbnail-rail', CvThumbnailRail);

  class CvProductPage extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.hover = matchMedia('(min-width:990px) and (hover:hover) and (pointer:fine)');
      this.mobile = matchMedia('(max-width:749px)');
      this.dialog = this.querySelector('.cv-size-dialog');
      this.info = this.querySelector('.product__info-container');
      this.guideContent = this.querySelector('[data-cv-guide-content]');
      this.hasGuide = Boolean(this.guideContent?.textContent.trim() || this.guideContent?.querySelector('img'));
      this.explicitGuide = this.hasGuide;
      this.prepareDescription();
      this.refresh();
      this.addEventListener('click', (event) => {
        const guide = event.target.closest('[data-cv-size-guide]');
        if (guide && this.hasGuide && this.dialog.showModal) {
          event.preventDefault(); this.opener = guide; this.endZoom();
          history.pushState({ ...history.state, cvSizeGuide: this.dataset.section }, '', location.href);
          this.dialog.showModal();
          this.savedOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
        }
        if (event.target.closest('[data-cv-close-guide]')) this.closeGuide();
        if (event.target.closest('[data-cv-sticky-add]')) {
          const button = this.querySelector('product-form [type="submit"]');
          if (button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') { this.submittedFromSticky = true; button.click(); }
        }
        if (event.target.closest('.thumbnail')) this.endZoom();
      }, options);
      this.dialog?.addEventListener('cancel', (event) => { event.preventDefault(); this.closeGuide(); }, options);
      this.dialog?.addEventListener('click', (event) => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.closeGuide();
      }, options);
      this.dialog?.addEventListener('close', () => {
        document.body.style.overflow = this.savedOverflow || '';
        (this.opener?.isConnected ? this.opener : this.querySelector('[data-cv-size-guide]'))?.focus({ preventScroll:true });
      }, options);
      window.addEventListener('popstate', () => { if (this.dialog?.open) this.dialog.close(); }, options);
      this.addEventListener('pointerover', (event) => this.startZoom(event), options);
      this.addEventListener('pointermove', (event) => this.moveZoom(event), options);
      this.addEventListener('pointerout', (event) => {
        if (this.zoom && !this.zoom.contains(event.relatedTarget)) this.endZoom();
      }, options);
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.endZoom(); }, options);
      this.hover.addEventListener('change', () => this.endZoom(), options);
      this.mobile.addEventListener('change', () => this.refreshSticky(), options);
      window.addEventListener('scroll', () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => { this.stickyFrame = null; this.refreshSticky(); });
      }, { ...options, passive:true });
      this.observer = new MutationObserver(() => this.scheduleRefresh());
      if (this.info) this.observer.observe(this.info, { childList:true, subtree:true, attributes:true, attributeFilter:['disabled','aria-disabled'] });
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId === this.dataset.section) { this.endZoom(); this.scheduleRefresh(); }
        });
      }
      this.observePurchase();
    }
    closeGuide() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      if (history.state?.cvSizeGuide === this.dataset.section) history.back();
    }
    scheduleRefresh() {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refresh());
    }
    refresh() { this.refreshGuideLinks(); this.groupMedia(); this.refreshSticky(); }
    refreshGuideLinks() {
      if (!this.hasGuide) return;
      let links = [...this.querySelectorAll('[data-cv-size-guide]')];
      if (!links.length && this.info) {
        const link = document.createElement('a'); link.className = 'cv-size-guide-link cv-size-guide-fallback';
        link.setAttribute('data-cv-size-guide', ''); link.href = '#'; link.textContent = 'Size guide';
        const target = this.info.querySelector('variant-selects') || this.info.querySelector('product-form');
        target?.before(link); links = [link];
      }
      const inline = links.find(link => !link.classList.contains('cv-size-guide-fallback'));
      links.forEach(link => {
        link.hidden = Boolean(inline && link.classList.contains('cv-size-guide-fallback'));
        link.setAttribute('aria-haspopup', 'dialog'); link.setAttribute('aria-controls', this.dialog.id);
      });
    }
    prepareDescription() {
      const description = this.querySelector('[data-cv-description]');
      if (!description) return;
      if (!this.explicitGuide && this.dataset.extractChart === 'true') {
        const candidates = [...description.querySelectorAll('table')].filter(table => {
          const rows = [...table.rows];
          const heading = table.previousElementSibling;
          if (!heading || !/^product measurements\b/i.test(heading.textContent.trim())) return false;
          if (!/inches|centimeters|centimetres|\bcm\b/i.test(heading.textContent)) return false;
          if (rows.length < 2 || rows[0].cells.length < 2 || normal(rows[0].cells[0].textContent) !== 'size') return false;
          if (![...rows[0].cells].some(cell => /^(waist|hip|bust|chest|inseam|length|bottom length|top length|sleeve length)$/i.test(cell.textContent.trim()))) return false;
          return rows.slice(1).every(row => row.cells.length === rows[0].cells.length && [...row.cells].every(cell => cell.textContent.trim()));
        });
        // Multiple or unfamiliar charts stay in the original description: no guessing.
        if (candidates.length === 1) {
          const table = candidates[0];
          const heading = table.previousElementSibling;
          const inches = /inches/i.test(heading.textContent);
          const unitLabel = document.createElement('p');
          unitLabel.textContent = `Product measurements · ${inches ? 'inches' : 'centimetres'}`;
          this.guideContent.append(unitLabel, table);
          heading.remove(); this.hasGuide = true;
          if (inches) this.addUnitSwitch(table, unitLabel);
        }
      }
      const care = this.querySelector('[data-cv-care-content]');
      const useImportedCare = care && !care.textContent.trim();
      const careList = document.createElement('ul');
      description.querySelectorAll('li').forEach(item => {
        if (item.children.length) return;
        const match = item.textContent.match(/^\s*(Features|Material composition|Care instructions):\s*(.+)$/i);
        if (!match) return;
        const label = normal(match[1]) === 'material composition' ? 'Composition' : normal(match[1]) === 'care instructions' ? 'Care' : 'Features';
        item.textContent = `${label}: ${match[2].replace(/,\s*/g, ', ')}`;
        if (useImportedCare && label !== 'Features') careList.append(item);
      });
      if (careList.children.length) { care.append(careList); care.closest('[data-cv-care]').hidden = false; }
      description.querySelectorAll('ul,ol').forEach(list => { if (!list.children.length) list.remove(); });
      if (!description.textContent.trim() && !description.querySelector('img,video,table')) description.closest('details').hidden = true;
    }
    addUnitSwitch(table, label) {
      // Never convert size-conversion columns (US, UK, EU) into centimetres.
      if (![...table.rows[0].cells].slice(1).every(cell => /^(waist|hip|bust|chest|inseam|length|bottom length|top length|sleeve length)$/i.test(cell.textContent.trim()))) return;
      const cells = [...table.rows].slice(1).flatMap(row => [...row.cells].slice(1));
      if (!cells.every(cell => /^\d+(\.\d+)?$/.test(cell.textContent.trim()))) return;
      const values = cells.map(cell => cell.textContent.trim());
      const controls = document.createElement('div'); controls.className = 'cv-size-units';
      controls.setAttribute('role','group'); controls.setAttribute('aria-label','Measurement units');
      ['in','cm'].forEach(unit => {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = unit;
        button.setAttribute('aria-pressed', String(unit === 'in'));
        button.addEventListener('click', () => {
          controls.querySelectorAll('button').forEach(other => other.setAttribute('aria-pressed', String(other === button)));
          cells.forEach((cell,index) => { cell.textContent = unit === 'in' ? values[index] : (Number(values[index]) * 2.54).toFixed(1); });
          label.textContent = `Product measurements · ${unit === 'in' ? 'inches' : 'centimetres (converted, rounded to 0.1 cm)'}`;
        });
        controls.append(button);
      });
      table.before(controls);
    }
    groupMedia() {
      const mapping = this.querySelector('[data-cv-media-mapping]')?.content;
      const gallery = this.querySelector('media-gallery');
      if (!mapping || !gallery) return;
      const colors = [...mapping.querySelectorAll('[data-cv-color]')].map(item => item.dataset);
      if (colors.length < 2) return;
      const inputs = [...this.querySelectorAll('variant-selects input:checked, variant-selects option:checked')];
      const selected = inputs.find(input => input.dataset.optionName === colors[0].optionName)?.value;
      const known = new Map();
      colors.forEach(color => { if (color.id) { const names = known.get(color.id) || new Set(); names.add(normal(color.name)); known.set(color.id,names); } });
      const media = [...mapping.querySelectorAll('[data-cv-media]')];
      let fullyMapped = true;
      media.forEach(item => {
        const prefix = item.dataset.alt.includes(' | ') ? normal(item.dataset.alt.split(' | ')[0]) : '';
        if (prefix === 'all colours' || prefix === 'all colors') known.set(item.dataset.id, new Set(['*']));
        else if (colors.some(color => normal(color.name) === prefix)) known.set(item.dataset.id, new Set([prefix]));
        if (!known.has(item.dataset.id)) fullyMapped = false;
      });
      const hasMatch = [...known.values()].some(names => names.has(normal(selected)));
      const filter = this.dataset.groupMedia === 'true' && fullyMapped && hasMatch;
      gallery.querySelectorAll('[data-media-id],[data-target]').forEach(item => {
        const key = (item.dataset.mediaId || item.dataset.target).replace(`${this.dataset.section}-`,'');
        const names = known.get(key);
        item.toggleAttribute('data-cv-filtered', Boolean(filter && names && !names.has('*') && !names.has(normal(selected))));
      });
      const note = gallery.querySelector('[data-cv-gallery-note]');
      if (note) note.hidden = filter;
      gallery.querySelector('cv-thumbnail-rail')?.resetPages();
      gallery.querySelector('[id^="GalleryViewer"]')?.resetPages?.();
      const active = gallery.querySelector('.product__media-item.is-active');
      if (active?.hasAttribute('data-cv-filtered')) {
        const first = [...gallery.querySelectorAll('.product__media-item')].find(item => !item.hasAttribute('data-cv-filtered'));
        if (first) gallery.setActiveMedia?.(first.dataset.mediaId, false);
      }
    }
    startZoom(event) {
      if (this.dataset.hoverZoom !== 'true' || !this.hover.matches || event.pointerType === 'touch') return;
      const opener = event.target.closest('.product__modal-opener--image');
      if (!opener || opener.contains(event.relatedTarget)) return;
      const source = opener.querySelector('[data-cv-zoom-source]')?.content.textContent.trim();
      const frame = opener.querySelector('.product__media');
      if (!source || !frame) return;
      this.endZoom(); this.zoom = opener;
      let layer = frame.querySelector('.cv-zoom-layer');
      if (!layer) {
        layer = document.createElement('img'); layer.className = 'cv-zoom-layer'; layer.alt = ''; layer.setAttribute('aria-hidden','true');
        layer.decoding = 'async'; layer.src = source; frame.append(layer);
      }
      const reveal = () => { if (this.zoom === opener && layer.naturalWidth) opener.classList.add('cv-zoom-active'); };
      if (layer.complete) reveal(); else layer.addEventListener('load', reveal, { once:true });
      this.moveZoom(event);
    }
    moveZoom(event) {
      if (!this.zoom) return;
      const frame = this.zoom.querySelector('.product__media');
      const rect = frame.getBoundingClientRect();
      frame.style.setProperty('--cv-zoom-x', `${Math.max(0,Math.min(100,(event.clientX-rect.left)/rect.width*100))}%`);
      frame.style.setProperty('--cv-zoom-y', `${Math.max(0,Math.min(100,(event.clientY-rect.top)/rect.height*100))}%`);
    }
    endZoom() { this.zoom?.classList.remove('cv-zoom-active'); this.zoom = null; }
    observePurchase() {
      const button = this.querySelector('product-form [type="submit"]');
      if (!button || !this.querySelector('[data-cv-sticky-purchase]')) return;
      this.purchaseObserver = new IntersectionObserver(([entry]) => {
        this.pastPurchase = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.refreshSticky();
      });
      this.purchaseObserver.observe(button);
    }
    refreshSticky() {
      const bar = this.querySelector('[data-cv-sticky-purchase]');
      const original = this.querySelector('product-form [type="submit"]');
      if (!bar || !original) return;
      const error = this.querySelector('product-form .product-form__error-message-wrapper');
      if (this.submittedFromSticky && error && !error.hidden && error.textContent.trim()) {
        this.submittedFromSticky = false;
        error.scrollIntoView({ block:'center', behavior:'auto' });
        error.setAttribute('tabindex','-1'); error.focus({ preventScroll:true });
      }
      bar.hidden = !this.mobile?.matches || original.getBoundingClientRect().bottom >= 0;
      const button = bar.querySelector('button'); button.disabled = original.disabled || original.getAttribute('aria-disabled') === 'true';
      text(button, original.querySelector('span')?.textContent.trim() || 'Add to bag');
      const price = this.info?.querySelector('.price');
      const amount = price?.querySelector(price.classList.contains('price--on-sale') ? '.price-item--sale' : '.price-item--regular');
      text(bar.querySelector('[data-cv-sticky-price]'), price?.classList.contains('hidden') ? '' : amount?.textContent.trim() || '');
      const values = [...this.querySelectorAll('variant-selects input:checked, variant-selects option:checked')].map(input => input.value);
      text(bar.querySelector('[data-cv-sticky-selection]'), values.join(' / '));
    }
    disconnectedCallback() {
      this.endZoom(); this.events?.abort(); this.observer?.disconnect(); this.purchaseObserver?.disconnect(); this.unsubscribe?.();
      cancelAnimationFrame(this.refreshFrame);
      cancelAnimationFrame(this.stickyFrame);
      if (this.dialog?.open) { this.dialog.close(); document.body.style.overflow = this.savedOverflow || ''; }
      this.events = null;
    }
  }
  customElements.define('cv-product-page', CvProductPage);
})();
